import openai, { OPENAI_MODELS } from './openaiClient';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Generates AI chatbot response for travel questions about Philippines.
 * @param {Array} messages - Conversation history with role and content.
 * @param {Object} userPreferences - User preferences for personalization.
 * @returns {Promise<string>} AI assistant response.
 */
export async function getChatbotResponse(messages, userPreferences = {}) {
  try {
    const systemPrompt = `You are an expert travel assistant specializing in the Philippines. Your role is to:

1. Answer travel questions about Philippine destinations, culture, customs, and attractions
2. Suggest activities based on user preferences (adventure, relaxation, culture, food, nature)
3. Provide personalized recommendations for hotels, restaurants, and experiences
4. Offer practical travel tips (best time to visit, transportation, budget advice)
5. Share insights about local culture, festivals, and hidden gems

User Preferences:
${userPreferences?.budget ? `- Budget: PHP ${userPreferences?.budget}` : ''}
${userPreferences?.destination ? `- Interested in: ${userPreferences?.destination}` : ''}
${userPreferences?.travelStyle ? `- Travel Style: ${userPreferences?.travelStyle}` : ''}

Be friendly, informative, and enthusiastic about Philippine travel. Keep responses concise but helpful.`;

    const response = await openai?.chat?.completions?.create({
      model: OPENAI_MODELS?.MINI,
      messages: [
        { role: 'system', content: systemPrompt },
        ...messages
      ],
      temperature: 0.7,
      max_tokens: 500,
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in chatbot response:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Streams chatbot response in real-time for dynamic user experience.
 * @param {Array} messages - Conversation history.
 * @param {Function} onChunk - Callback to handle each streamed chunk.
 * @param {Object} userPreferences - User preferences for personalization.
 */
export async function streamChatbotResponse(messages, onChunk, userPreferences = {}) {
  try {
    const systemPrompt = `You are an expert travel assistant specializing in the Philippines. Your role is to:

1. Answer travel questions about Philippine destinations, culture, customs, and attractions
2. Suggest activities based on user preferences (adventure, relaxation, culture, food, nature)
3. Provide personalized recommendations for hotels, restaurants, and experiences
4. Offer practical travel tips (best time to visit, transportation, budget advice)
5. Share insights about local culture, festivals, and hidden gems

User Preferences:
${userPreferences?.budget ? `- Budget: PHP ${userPreferences?.budget}` : ''}
${userPreferences?.destination ? `- Interested in: ${userPreferences?.destination}` : ''}
${userPreferences?.travelStyle ? `- Travel Style: ${userPreferences?.travelStyle}` : ''}

Be friendly, informative, and enthusiastic about Philippine travel. Keep responses concise but helpful.`;

    const stream = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini',
      messages: [
        { role: 'system', content: systemPrompt },
        ...messages
      ],
      stream: true,
      reasoning_effort: 'minimal',
    });

    for await (const chunk of stream) {
      const content = chunk?.choices?.[0]?.delta?.content || '';
      if (content) {
        onChunk(content);
      }
    }
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error in streaming chatbot response:', error);
    }
    throw new Error(errorInfo.message);
  }
}
